import { motion } from "framer-motion";
import { LANGS, type Lang } from "@rekati/i18n";
import { spring } from "@rekati/tokens";
import { cn } from "@rekati/utils";
import { useI18n } from "../../content/i18n";

/**
 * EN / ID as two pressed-state buttons (a radio group would need arrow-key handling for two options). Switching the page's language is
 * local; a signed-in attendee's email language follows only from the
 * account page's explicit choice (`onPick`), so a curious tap never changes
 * what the backend mails.
 */
export function LanguageToggle({
  label,
  onPick,
  className,
}: {
  label: string;
  onPick?: (lang: Lang) => void;
  className?: string;
}) {
  const { lang, setLang } = useI18n();
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("relative flex rounded-full border border-border p-0.5", className)}
    >
      {LANGS.map((l) => {
        const active = l === lang;
        return (
          <button
            key={l}
            type="button"
            aria-pressed={active}
            aria-label={l === "en" ? "English" : "Bahasa Indonesia"}
            lang={l}
            onClick={() => {
              setLang(l);
              onPick?.(l);
            }}
            className={cn(
              "relative z-10 h-8 min-w-11 rounded-full px-3 font-mono text-xs font-medium tracking-wider uppercase transition-colors",
              active ? "text-volt-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {active ? (
              <motion.span
                layoutId="lang-pill"
                className="absolute inset-0 -z-10 rounded-full bg-volt"
                transition={spring.snappy}
              />
            ) : null}
            {l}
          </button>
        );
      })}
    </div>
  );
}
